import type { AssistantMessage, AssistantSession } from './assistant-models'

export const ASSISTANT_PENDING_ACTION_TEXT = '等待处理请求'
export const ASSISTANT_RUN_INCOMPLETE_PREFIX = '运行未完成：'

function withoutMessage(
  messages: AssistantMessage[],
  messageId: string
): AssistantMessage[] {
  return messages.filter((message) => message.id !== messageId)
}

function createAssistantText(
  messageId: string,
  text: string
): AssistantMessage {
  return {
    id: messageId,
    role: 'assistant',
    kind: 'text',
    time: new Date().toTimeString().slice(0, 5),
    text,
  }
}

export function beginAssistantTurn(
  session: AssistantSession,
  typingMessageId: string
): AssistantSession {
  return {
    ...session,
    status: 'running',
    messages: [
      ...session.messages,
      { id: typingMessageId, role: 'assistant', kind: 'typing', time: '' },
    ],
  }
}

export function applyAssistantStart(
  session: AssistantSession,
  event: { conversationId: string; runId: string }
): AssistantSession {
  return {
    ...session,
    conversationId: event.conversationId,
    runId: event.runId,
    status: 'running',
  }
}

export function applyAssistantQueued(
  session: AssistantSession,
  event: {
    conversationId?: string
    runId: string
    queueSequence?: number
    queuePosition?: number
    typingMessageId: string
    messageId: string
  }
): AssistantSession {
  const position =
    typeof event.queuePosition === 'number'
      ? `，当前第 ${event.queuePosition} 位`
      : ''
  return {
    ...session,
    conversationId: event.conversationId || session.conversationId,
    runId: event.runId,
    queueSequence: event.queueSequence,
    queuePosition: event.queuePosition,
    status: 'queued',
    messages: [
      ...withoutMessage(session.messages, event.typingMessageId),
      createAssistantText(event.messageId, `请求已进入队列${position}`),
    ],
  }
}

export function appendAssistantDelta(
  session: AssistantSession,
  event: { typingMessageId: string; messageId: string; content: string }
): AssistantSession {
  const messages = withoutMessage(session.messages, event.typingMessageId)
  if (!messages.some((message) => message.id === event.messageId)) {
    return {
      ...session,
      messages: [
        ...messages,
        createAssistantText(event.messageId, event.content),
      ],
    }
  }

  return {
    ...session,
    messages: messages.map((message) =>
      message.id === event.messageId
        ? { ...message, text: `${message.text || ''}${event.content}` }
        : message
    ),
  }
}

export function applyAssistantRequiresAction(
  session: AssistantSession,
  event: { typingMessageId: string; runId: string; messageId: string }
): AssistantSession {
  const messages = withoutMessage(session.messages, event.typingMessageId)
  return {
    ...session,
    runId: event.runId,
    status: 'awaiting-action',
    messages: event.typingMessageId
      ? [
          ...messages,
          createAssistantText(event.messageId, ASSISTANT_PENDING_ACTION_TEXT),
        ]
      : messages,
  }
}

export function applyAssistantDone(
  session: AssistantSession,
  event: {
    typingMessageId: string
    runId: string
    status: string
    messageId: string
    finalReply?: string | null
  }
): AssistantSession {
  const messages = withoutMessage(session.messages, event.typingMessageId)
  const runId = event.runId
  if (event.status !== 'completed') {
    const failureMessageId = messages.some(
      (message) => message.id === event.messageId
    )
      ? `${event.messageId}-failure`
      : event.messageId
    return {
      ...session,
      runId,
      status: 'failed',
      messages: [
        ...messages,
        createAssistantText(
          failureMessageId,
          `${ASSISTANT_RUN_INCOMPLETE_PREFIX}${event.status}`
        ),
      ],
    }
  }

  const finalReply = event.finalReply?.trim()
  const hasStreamedMessage = messages.some(
    (message) => message.id === event.messageId
  )
  if (!finalReply || hasStreamedMessage) {
    return { ...session, runId, status: 'completed', messages }
  }

  return {
    ...session,
    runId,
    status: 'completed',
    messages: [...messages, createAssistantText(event.messageId, finalReply)],
  }
}

export function failAssistantRun(
  session: AssistantSession,
  event: { typingMessageId: string; messageId: string; message: string }
): AssistantSession {
  return {
    ...session,
    status: 'failed',
    messages: [
      ...withoutMessage(session.messages, event.typingMessageId),
      createAssistantText(event.messageId, event.message),
    ],
  }
}

/**
 * Replaces a failed client Tool card with one visible assistant error message.
 * A failed card has no usable data and must not coexist with a duplicate error.
 */
export function failAssistantToolRun(
  session: AssistantSession,
  event: {
    typingMessageId: string
    toolCallId: string
    messageId: string
    message: string
  }
): AssistantSession {
  return {
    ...session,
    status: 'failed',
    messages: [
      ...session.messages.filter(
        (message) =>
          message.id !== event.typingMessageId &&
          message.toolCallId !== event.toolCallId
      ),
      createAssistantText(event.messageId, event.message),
    ],
  }
}
